import { type Locale as DateFnsLocale } from "date-fns";
import { enUS, ja } from "date-fns/locale";
import {
  enUS as coreEnUS,
  jaJP as coreJaJP,
  type Localization as CoreLocalization,
} from "@mui/material/locale";
import {
  enUS as gridEnUS,
  jaJP as gridJaJP,
} from "@mui/x-data-grid-pro/locales";
import {
  enUS as pickersEnUS,
  jaJP as pickersJaJP,
} from "@mui/x-date-pickers/locales";

interface LocaleConfig {
  // date-fns locale object consumed by date formatters and the MUI date picker.
  dateFnsLocale: DateFnsLocale;
  // Human-readable label shown in locale pickers.
  label: string;
}

export const SUPPORTED_LANGUAGES = ["en", "ja"] as const;
export type SUPPORTED_LANGUAGE = (typeof SUPPORTED_LANGUAGES)[number];
type SUPPORTED_LOCALE = "en-US" | "ja-JP";

// Single source of truth for supported end-user formatting locales (BCP-47),
// distinct from the i18n UI language (see features/ui/LanguageSelect). To support
// a new locale, add one entry here — every locale-aware mapping in the app
// (date-fns locale, picker locale, picker labels, fallback) derives from it.
export const SUPPORTED_LOCALES = {
  "en-US": { dateFnsLocale: enUS, label: "English (US)" },
  "ja-JP": { dateFnsLocale: ja, label: "日本語 (JP)" },
} satisfies Record<SUPPORTED_LOCALE, LocaleConfig>;

interface MuiLocales {
  core: CoreLocalization;
  dataGrid: typeof gridEnUS;
  pickers: typeof pickersEnUS;
}

// MUI built-in text (Autocomplete, pagination, DataGrid, date pickers) per UI
// language. To support a new UI language, add one entry here.
export const MUI_LOCALE_MAPPING: Record<SUPPORTED_LANGUAGE, MuiLocales> = {
  en: { core: coreEnUS, dataGrid: gridEnUS, pickers: pickersEnUS },
  ja: { core: coreJaJP, dataGrid: gridJaJP, pickers: pickersJaJP },
};

export type SupportedLocale = keyof typeof SUPPORTED_LOCALES;

// Fallback when a user's resolved locale is not supported.
export const DEFAULT_LOCALE: SupportedLocale = "en-US";
